import { Mail, Phone, Sms } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RecipientSubTypeEnum } from "../../../../../common/enums/RecipientSubTypeEnum";
import { RecipientTypeEnum } from "../../../../../common/enums/RecipientTypeEnum";
import RecipientEditDialog, { OpenMode, bindRecipientEditDialog, useRecipientEditDialogState } from "../../../../../components/recipient-edit-dialog/RecipientEditDialog";
import { useConfirmation } from "../../../../../contexts/confirmation-context/ConfirmationContext";
import Enumerable from "linq";
import TwoStepConfirmationDialog, { bindTwoStepConfirmationDialog, useTwoStepConfirmationDialogState } from "../../../../../components/two-step-confirmation-dialog/TwoStepConfirmationDialog";
import { getRecipientTypeName } from "../../../../../common/utilities/GetRecipientTypeName";
import NoData from "../../../../../components/no-data/NoData";
import UserRecipientList from "../../../../../components/user-recipient-list/UserRecipientList";
import { UserRecipientFieldArrayType, UserRecipientFieldType } from "../../../../../components/user-recipient-list/UserRecipientFieldTypes";
import { UserFieldType } from "../UserFieldTypes";

import type { JSX } from "react";

export type AlarmNotificationsStepProps = {
    organizationID: number | undefined;
};

export default function AlarmNotificationsStep({
    organizationID
}: AlarmNotificationsStepProps): JSX.Element {

    const { t } = useTranslation();
    const {
        watch
    } = useFormContext<UserFieldType>();
    const { confirm } = useConfirmation();

    const recipientEditDialog = useRecipientEditDialogState();
    const disableConfirmationDialog = useTwoStepConfirmationDialogState();

    const {
        fields,
        append,
        remove,
        update
    } = useFieldArray<UserFieldType, "recipients", "fieldID">({ name: "recipients", keyName: "fieldID" });

    const watchRecipients = watch("recipients");

    const recipients: UserRecipientFieldArrayType =
        fields.map((field, index) => {
            return {
                ...field,
                ...watchRecipients[index],
                recipientKey: `recipients.${index}`
            };
        });

    const emailRecipients = Enumerable
        .from(recipients)
        .where(r => r.type === RecipientTypeEnum.Email)
        .orderBy(a => a.to)
        .toArray();
    const smsRecipients = Enumerable
        .from(recipients)
        .where(r => r.type === RecipientTypeEnum.Text)
        .orderBy(a => a.to)
        .toArray();
    const voiceRecipients = Enumerable
        .from(recipients)
        .where(r => r.type === RecipientTypeEnum.Voice)
        .orderBy(a => a.to)
        .toArray();

    async function addRecipient() {

        let id = fields.length > 0 ? Math.min(...fields.map(o => o.id)) - 1 : -1;
        if (id > 0) {
            id = -1;
        }

        const recipient: UserRecipientFieldType = {
            id: id,
            type: RecipientTypeEnum.Email,
            subType: RecipientSubTypeEnum.None,
            to: "",
            disabled: false,
            disabledTimestamp: undefined,
            usageCount: 0,
            cloneRecipientID: undefined
        };

        const results = await recipientEditDialog.open({
            openMode: OpenMode.Add,
            organizationID: organizationID,
            recipientID: recipient.id,
            recipientType: recipient.type,
            recipientSubType: recipient.subType,
            recipientTo: recipient.to,
            allowCloneRecipientUsage: true
        });

        if (results.reason === "ok") {
            const updatedRecipient: UserRecipientFieldType = {
                ...recipient,
                type: results.values?.recipientType as RecipientTypeEnum,
                subType: results.values?.recipientSubType as RecipientSubTypeEnum,
                to: results.values?.recipientTo as string,
                cloneRecipientID: results.values?.cloneRecipientID
            };

            append(updatedRecipient);
        }
    }

    async function editRecipient(recipientKey: string) {

        const index = recipients.findIndex(r => r.recipientKey === recipientKey);
        const recipient = fields[index];

        if (recipient) {
            const results = await recipientEditDialog.open({
                openMode: OpenMode.Edit,
                organizationID: organizationID,
                recipientID: recipient.id,
                recipientType: recipient.type,
                recipientSubType: recipient.subType,
                recipientTo: recipient.to,
                allowCloneRecipientUsage: true,
                cloneRecipientID: recipient.cloneRecipientID
            });

            if (results.reason === "ok" && results.values) {
                const editedRecipient: UserRecipientFieldType = {
                    ...recipient,
                    subType: results.values.recipientSubType,
                    to: results.values.recipientTo,
                    cloneRecipientID: results.values.cloneRecipientID
                };

                update(index, editedRecipient);
            }
        }
    }

    async function disableRecipient(recipientKey: string, disable: boolean) {

        const index = recipients.findIndex(r => r.recipientKey === recipientKey);

        if (index > -1) {

            const recipient = fields[index];

            if (disable && !recipient.disabledTimestamp) {

                const recipientTypeName = getRecipientTypeName(recipient.type);

                const result = await disableConfirmationDialog.open({
                    title: t("userEditor.steps.alarmNotifications.disableConfirmation.title"),
                    explanation: (
                        <Stack direction="column" spacing={2}>
                            <Typography>
                                {t("userEditor.steps.alarmNotifications.disableConfirmation.explanation1", { recipientType: recipientTypeName, recipientTo: recipient.to })}
                            </Typography>
                            <Typography sx={{
                                fontWeight: "bold"
                            }}>
                                {t("userEditor.steps.alarmNotifications.disableConfirmation.explanation2", { recipientType: recipientTypeName, recipientTo: recipient.to })}
                            </Typography>
                        </ Stack>
                    ),
                    confirmationText: t("userEditor.steps.alarmNotifications.disableConfirmation.confirmationText", { recipientType: recipientTypeName })
                });

                if (result.reason === "ok") {
                    update(index, {
                        ...recipient,
                        disabled: disable
                    });
                }
            } else {
                update(index, {
                    ...recipient,
                    disabled: disable
                });
            }
        }
    }

    async function deleteRecipient(recipientKey: string) {
        const shouldDelete = await getDeleteRecipientConfirmation();

        if (shouldDelete) {

            const index = recipients.findIndex(r => r.recipientKey === recipientKey);

            if (index >= -1) {
                remove(index);
            }
        }
    }

    async function getDeleteRecipientConfirmation() {

        return (await confirm({
            dialogTitle: t("deleteConfirmation.title", { itemName: t("dataTypeNames.recipient") }),
            confirmationText: t("deleteConfirmation.confirmationText"),
            yesButtonText: t("deleteConfirmation.yesButtonText"),
            noButtonText: t("deleteConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }

    return (
        <Box
            sx={{
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                flex: "1 1 auto",
                backgroundColor: theme => theme.palette.custom.background.cardContainer
            }}
        >
            <Stack
                direction="row"
                sx={{
                    alignItems: "center",
                    justifyContent: "flex-end",
                    flex: "0 0 auto",
                    padding: 1
                }}>
                <Button onClick={() => addRecipient()}>
                    {t("userEditor.steps.alarmNotifications.addRecipient")}
                </Button>

            </Stack>
            <Stack
                direction="column"
                spacing={1}
                sx={{
                    flex: "1 1 auto",
                    paddingLeft: 1,
                    paddingRight: 1,
                    overflowY: "auto"
                }}>
                {recipients.length === 0 &&
                    <NoData
                        title={t("userEditor.steps.alarmNotifications.noRecipients.title")}
                        text={t("userEditor.steps.alarmNotifications.noRecipients.text")}
                    />
                }

                {emailRecipients.length > 0 &&
                    <UserRecipientList
                        title={t("userEditor.steps.alarmNotifications.emailNotifications")}
                        icon={<Mail />}
                        recipients={emailRecipients}
                        onEdit={editRecipient}
                        onDisable={disableRecipient}
                        onDelete={deleteRecipient}
                    />
                }

                {smsRecipients.length > 0 &&
                    <UserRecipientList
                        title={t("userEditor.steps.alarmNotifications.smsNotifications")}
                        icon={<Sms />}
                        recipients={smsRecipients}
                        onEdit={editRecipient}
                        onDisable={disableRecipient}
                        onDelete={deleteRecipient}
                    />
                }

                {voiceRecipients.length > 0 &&
                    <UserRecipientList
                        title={t("userEditor.steps.alarmNotifications.voiceNotifications")}
                        icon={<Phone />}
                        recipients={voiceRecipients}
                        onEdit={editRecipient}
                        onDisable={disableRecipient}
                        onDelete={deleteRecipient}
                    />
                }
            </Stack>
            <RecipientEditDialog {...bindRecipientEditDialog(recipientEditDialog)} />
            <TwoStepConfirmationDialog {...bindTwoStepConfirmationDialog(disableConfirmationDialog)} />
        </Box >
    );
}




